import React from "react";
import { Helmet } from "react-helmet";
import { Link, useHistory } from "react-router-dom";
import ArtisanSideView from "assets/artisan-side-view.png";
import { FaArrowRight } from "react-icons/fa";
import artisansLaughing from "assets/artisans-laughing.png";
import artisanAndClient from "assets/artisan-and-client.png";
import artisanOnLadder from "assets/artisan-on-ladder.png";
import artisanObservingShipment from "assets/artisan-observing-shipment.jpeg";
import { ApplicationConfig } from "context/ApplicationConfig";
import { isLeanServicesModeEnabled } from "helpers/lean-services";

type AuthGatewayMode = "signup" | "login";

export interface AuthGatewayProps {
  mode?: AuthGatewayMode;
}

const openInNewTab = (url?: string) => {
  if (!url) return;
  window.open(url, "_blank");
};

// Tile Image Component
const TileImage: React.FC<{ src: string; alt: string; className?: string }> = ({
  src,
  alt,
  className = "w-12 h-12 rounded-full object-cover",
}) => <img src={src} alt={alt} className={className} />;

export const AuthGateway: React.FC<AuthGatewayProps> = ({
  mode = "signup",
}) => {
  const { push } = useHistory();
  const { toggles } = React.useContext(ApplicationConfig);

  const isLogin = mode === "login";
  const isLeanServicesMode = isLeanServicesModeEnabled(toggles);

  const handleFindTalentBusinesses = () => {
    openInNewTab(process.env.REACT_APP_MARKETPLACE_URL);
  };

  const handleFindTalentHomeowner = () => {
    openInNewTab(process.env.REACT_APP_HOMEOWNER_DASHBOARD_URL);
  };

  const handleFindWork = () => {
    openInNewTab(process.env.REACT_APP_PRO_APPLICATION_URL);
  };

  const handleManageTalents = () => {
    push("/community-manager?talk-to-sales=true");
  };

  return (
    <>
      <Helmet>
        <title>{isLogin ? "Login | LaborHack" : "Sign up | LaborHack"}</title>
      </Helmet>
      <div className="min-h-screen bg-white grid grid-cols-1 lg:grid-cols-2">
        {/* Left Side */}
        <div className="hidden lg:block relative bg-white overflow-hidden">
          <img
            src={ArtisanSideView}
            alt="Artisan side view"
            className="w-full h-[100vh] object-cover"
          />
        </div>

        {/* Right Side */}
        <div className="flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-[560px]">
            {/* Header */}
            <div className="text-left mb-8">
              <h1 className="text-[28px] font-medium text-[#040A0D] mb-3 font-aeonik">
                {isLogin ? "Log in to your account" : "Create your account"}
              </h1>
              <p className="text-lg text-[#464646]">
                {isLogin ? (
                  <>
                    Don&apos;t have an account yet?{" "}
                    <Link
                      to="/auth/register"
                      className="text-flatteredFlamingo-500 hover:text-flatteredFlamingo-600"
                    >
                      Sign up
                    </Link>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <Link
                      to="/auth/login"
                      className="text-flatteredFlamingo-500 hover:text-flatteredFlamingo-600"
                    >
                      Log in
                    </Link>
                  </>
                )}
              </p>
            </div>

            {/* Buttons */}
            <div className="space-y-6">
              {/* Find talents - Business */}
              <button
                type="button"
                onClick={handleFindTalentBusinesses}
                className="group w-full h-[82px] border border-[#CACACA] bg-white flex items-center px-4 hover:border-gray-400 hover:shadow-sm transition-all duration-200"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 rounded-full bg-flatteredFlamingo-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <TileImage
                      src={artisanObservingShipment}
                      alt="Businesses"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  </div>
                  <div className="text-left">
                    <div className="text-base text-[#464646] font-inter">
                      Find talent
                    </div>
                    <div className="text-lg font-medium text-[#2F1612] font-inter">
                      Businesses
                    </div>
                  </div>
                </div>
                <FaArrowRight className="w-4 h-4 lg:w-5 lg:h-5 text-[#2F1612] group-hover:translate-x-1 transition-transform duration-200" />
              </button>

              {!isLeanServicesMode && (
                <>
                  {/* Find talents - Homeowners */}
                  <button
                    type="button"
                    onClick={handleFindTalentHomeowner}
                    className="group w-full h-[82px] border border-[#CACACA] bg-white flex items-center px-4 hover:border-gray-400 hover:shadow-sm transition-all duration-200"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 rounded-full bg-flatteredFlamingo-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <TileImage
                          src={artisanAndClient}
                          alt="Homeowners"
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      </div>
                      <div className="text-left">
                        <div className="text-base text-[#464646] font-inter">
                          Find talent
                        </div>
                        <div className="text-lg font-medium text-[#2F1612] font-inter">
                          Homeowners
                        </div>
                      </div>
                    </div>
                    <FaArrowRight className="w-4 h-4 lg:w-5 lg:h-5 text-[#2F1612] group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                </>
              )}

              {/* Get jobs - Skilled workers */}
              <button
                type="button"
                onClick={handleFindWork}
                className="group w-full h-[82px] border border-[#CACACA] bg-white flex items-center px-4 hover:border-gray-400 hover:shadow-sm transition-all duration-200"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 rounded-full bg-flatteredFlamingo-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <TileImage
                      src={artisanOnLadder}
                      alt="Skilled workers"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  </div>
                  <div className="text-left">
                    <div className="text-base text-[#464646] font-inter">
                      Get jobs
                    </div>
                    <div className="text-lg font-medium text-[#2F1612] font-inter">
                      Skilled workers
                    </div>
                  </div>
                </div>
                <FaArrowRight className="w-4 h-4 lg:w-5 lg:h-5 text-[#2F1612] group-hover:translate-x-1 transition-transform duration-200" />
              </button>

              {!isLeanServicesMode && (
                <>
                  {/* Manage talent - Community */}
                  <button
                    type="button"
                    onClick={handleManageTalents}
                    className="group w-full h-[82px] border border-[#CACACA] bg-white flex items-center px-4 hover:border-gray-400 hover:shadow-sm transition-all duration-200"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 rounded-full bg-flatteredFlamingo-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <TileImage
                          src={artisansLaughing}
                          alt="Community"
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      </div>
                      <div className="text-left">
                        <div className="text-base text-[#464646] font-inter">
                          Manage talent
                        </div>
                        <div className="text-lg font-medium text-[#2F1612] font-inter">
                          Community
                        </div>
                      </div>
                    </div>
                    <FaArrowRight className="w-4 h-4 lg:w-5 lg:h-5 text-[#2F1612] group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                </>
              )}
            </div>

            {/* Terms */}
            <div className="mt-14 max-w-[560px]">
              <p className="text-[15px] leading-[1.8] text-[#464646] font-inter">
                By {isLogin ? "logging in" : "joining"}, you agree to the
                LaborHack{" "}
                <Link
                  to="/terms"
                  className="text-flatteredFlamingo-500 hover:text-flatteredFlamingo-600 hover:underline"
                >
                  Terms of Service
                </Link>{" "}
                and to occasionally receive emails from us. Please read our{" "}
                <Link
                  to="/privacy"
                  className="text-flatteredFlamingo-500 hover:text-flatteredFlamingo-600 hover:underline"
                >
                  Privacy Policy
                </Link>{" "}
                to learn how we use your personal data.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthGateway;
